import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import * as api from "../lib/claimsApi.ts";
import type { ExtractInput } from "../lib/claimsApi.ts";
import { ApiError, RateLimitError, genericApiError, isSentryWorthy } from "../lib/errors";
import type { ClaimWithSource } from "../types/claims";

interface UseExtractClaimsResult {
  isLoading: boolean;
  apiError: ApiError | null;
  rateLimitError: { retryAfter: number } | null;
  execute: (input: ExtractInput) => Promise<ClaimWithSource[] | null>;
}

export function useExtractClaims(): UseExtractClaimsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const [rateLimitError, setRateLimitError] = useState<{ retryAfter: number } | null>(null);

  const execute = useCallback(async (input: ExtractInput): Promise<ClaimWithSource[] | null> => {
    setIsLoading(true);
    setApiError(null);
    setRateLimitError(null);
    Sentry.addBreadcrumb({
      category: "api",
      message: "Extracting claims",
      level: "info",
    });
    try {
      const result = await api.extractClaims(input);
      return result;
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitError({ retryAfter: err.retryAfter ?? 3600 });
        return null;
      }
      const error = err instanceof ApiError ? err : genericApiError(err);
      setApiError(error);
      if (isSentryWorthy(err)) {
        Sentry.captureException(err, {
          tags: { "error.code": error.code },
          extra: { endpoint: "/claims/extract" },
        });
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, apiError, rateLimitError, execute };
}
